/**
 * Admin Organization Detail
 * Comprehensive tabs: Overview, Plan & Limits, Billing, UsersThree, Agents, Usage, GearSix
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminApiGet } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  ArrowLeft,
  ArrowSquareOut,
  ArrowsClockwise,
  Buildings,
  Calendar,
  ChartBar,
  CheckCircle,
  Clock,
  Copy,
  CurrencyDollar,
  Eye,
  GearSix,
  Key,
  Lightning,
  Microphone,
  PencilSimple,
  PlayCircle,
  Prohibit,
  Robot,
  Shield,
  Trash,
  TrendUp,
  User,
  UsersThree,
  WarningCircle
} from '@phosphor-icons/react';
const OrganizationDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchOrganizationDetail();
  }, [orgId]);

  const fetchOrganizationDetail = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet(`/api/admin/organizations/${orgId}`);
      // Backend returns { organization, agents, limits, recent_activity }
      // Merge agents into organization object for component compatibility
      const orgData = data.organization;
      if (data.agents) {
        orgData.agents = data.agents;
      }
      setOrg(orgData);
      
      // Set stats from the real data returned by the backend
      setStats({
        users: orgData.user_count || 0,
        agents: orgData.agent_count || 0,
        voiceMinutes30d: orgData.voice_minutes_30d || 0,
        llmTokens30d: orgData.llm_tokens_30d || 0,
        apiCalls30d: orgData.api_calls_30d || 0,
      });
    } catch (error) {
      console.error('Error fetching organization:', error);
      toast.error('Failed to load organization');
      navigate('/admin/organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationStats = async () => {
    // Stats are now returned as part of the organization detail endpoint
    // No separate fetch needed - stats come from the org object
  };

  const handleImpersonateOrg = () => {
    // TODO: Implement impersonation
    toast.info('Impersonation', 'Switching to organization view...');
  };

  const handleSuspendOrg = () => {
    // TODO: Implement suspension
    toast.warning('Suspend', 'Are you sure you want to suspend this organization?');
  };

  // Determine which tabs to show based on org properties
  const getTabs = () => {
    const baseTabs = [
      { id: 'overview', label: 'Overview', icon: Buildings },
      { id: 'plan', label: 'Owner Plan', icon: Shield },
      { id: 'users', label: 'UsersThree', icon: UsersThree },
      { id: 'agents', label: 'Agents', icon: Robot },
      { id: 'settings', label: 'Settings', icon: GearSix },
    ];
    
    // Only show billing for primary orgs (if they have billing history)
    if (org?.is_primary) {
      baseTabs.splice(2, 0, { id: 'billing', label: 'Billing History', icon: CurrencyDollar });
    }
    
    // Only show usage tab if there's actual usage data (not mock)
    if (stats && (stats.llmTokens30d > 0 || stats.voiceMinutes30d > 0)) {
      baseTabs.splice(-1, 0, { id: 'usage', label: 'Usage', icon: ChartBar });
    }
    
    return baseTabs;
  };

  const tabs = getTabs();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-white/10 rounded-xl animate-pulse"></div>
        <div className="h-96 bg-white/10 rounded-xl animate-pulse"></div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="text-center py-12">
        <WarningCircle size={48} className="text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Organization Not Found</h3>
        <button onClick={() => navigate('/admin/organizations')} className="text-[#6366F1] hover:underline">
          Back to Organizations
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/organizations')}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft size={20} />
        <span>Back to Organizations</span>
      </button>

      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
              <Buildings size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">{org.name}</h1>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-400">ID: {org.organization_id}</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(org.organization_id);
                    toast.success('Copied', 'Organization ID copied to clipboard');
                  }}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  org.tier === 'partner-pro' || org.tier === 'partner_pro'
                    ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                    : org.tier === 'pro'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                }`}>
                  {org.tier || 'basic'}
                </span>
                <span className={`px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${
                  org.status === 'suspended'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'bg-green-500/20 text-green-400 border border-green-500/30'
                }`}>
                  {org.status === 'suspended' ? <Prohibit size={12} /> : <CheckCircle size={12} />}
                  {org.status === 'suspended' ? 'Suspended' : 'Active'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleImpersonateOrg}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
            >
              <Eye size={16} />
              <span className="text-sm">Impersonate</span>
            </button>
            <button
              onClick={handleSuspendOrg}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
            >
              <Prohibit size={16} />
              <span className="text-sm">Suspend</span>
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-5 gap-4 pt-6 border-t border-white/10">
            <div>
              <p className="text-xs text-gray-500 mb-1">UsersThree</p>
              <p className="text-xl font-bold text-white">{stats.users}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Agents</p>
              <p className="text-xl font-bold text-white">{stats.agents}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Voice Minutes</p>
              <p className="text-xl font-bold text-white">{stats.voiceMinutes30d}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">LLM Tokens</p>
              <p className="text-xl font-bold text-white">{(stats.llmTokens30d / 1000).toFixed(1)}K</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">API Calls</p>
              <p className="text-xl font-bold text-white">{stats.apiCalls30d}</p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
        <div className="flex border-b border-white/10 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white/5 text-[#6366F1] border-b-2 border-[#6366F1]'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && <OverviewTab org={org} stats={stats} />}
          {activeTab === 'plan' && <OwnerPlanTab org={org} />}
          {activeTab === 'billing' && <BillingTab orgId={orgId} />}
          {activeTab === 'users' && <UsersTab orgId={orgId} />}
          {activeTab === 'agents' && <AgentsTab org={org} />}
          {activeTab === 'usage' && <UsageTab orgId={orgId} stats={stats} />}
          {activeTab === 'settings' && <SettingsTab org={org} onUpdate={fetchOrganizationDetail} />}
        </div>
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ org, stats }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 gap-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-400 mb-3">Organization Details</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-400">Name</span>
            <span className="text-white font-medium">{org.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Tier</span>
            <span className="text-white font-medium">{org.tier || 'basic'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Status</span>
            <span className="text-white font-medium">{org.status || 'active'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Created</span>
            <span className="text-white font-medium">
              {org.created_at ? new Date(org.created_at).toLocaleDateString() : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-400 mb-3">Owner Information</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-400">Owner ID</span>
            <span className="text-white font-medium">{org.owner_user_id || 'N/A'}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Owner Plan Tab Component (Read-Only)
const OwnerPlanTab = ({ org }) => {
  const [ownerData, setOwnerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOwnerPlan();
  }, [org.owner_user_id]);

  const fetchOwnerPlan = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet(`/api/admin/users/${org.owner_user_id}`);
      setOwnerData(data.user || data);
    } catch (error) {
      console.error('Error fetching owner:', error);
      toast.error('Failed to load owner information');
    } finally {
      setLoading(false);
    }
  };

  const formatPlanName = (plan) => {
    if (!plan) return 'Basic';
    const planMap = {
      'basic': 'Basic',
      'pro': 'Pro',
      'partner_pro': 'Partner Pro',
      'partner-pro': 'Partner Pro'
    };
    return planMap[plan] || plan;
  };

  const getPlanLimits = (plan) => {
    const limits = {
      'basic': { agents: 0, voice: 0, multiTenant: false },
      'pro': { agents: 3, voice: 1, multiTenant: false },
      'partner_pro': { agents: '∞', voice: '∞', multiTenant: true },
      'partner-pro': { agents: '∞', voice: '∞', multiTenant: true }
    };
    return limits[plan] || limits['basic'];
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-white/10 rounded-xl animate-pulse"></div>
        <div className="h-48 bg-white/10 rounded-xl animate-pulse"></div>
      </div>
    );
  }

  const ownerPlan = ownerData?.plan || 'basic';
  const limits = getPlanLimits(ownerPlan);

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <WarningCircle size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-blue-100 text-sm font-medium mb-1">
              Plan Management Moved to User Level
            </p>
            <p className="text-blue-200/80 text-sm">
              This organization inherits capabilities from its owner. To change the plan, 
              update it on the owner&apos;s user profile.
            </p>
          </div>
        </div>
      </div>

      {/* Owner Info Card */}
      <div className="bg-white/5 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Owner's Plan</h3>
          <button
            onClick={() => navigate(`/admin/users/${org.owner_user_id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#5558E3] rounded-lg transition-colors text-sm"
          >
            <User size={16} className="flex-shrink-0" />
            View Owner Profile
          </button>
        </div>

        {/* Owner Details */}
        <div className="mb-6 pb-6 border-b border-white/10">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
              <span className="text-white font-semibold text-lg">
                {ownerData?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div>
              <p className="text-white font-medium">{ownerData?.name || 'Unknown'}</p>
              <p className="text-gray-400 text-sm">{ownerData?.email || 'No email'}</p>
            </div>
          </div>
        </div>

        {/* Current Plan */}
        <div className="mb-6">
          <p className="text-gray-400 text-sm mb-2">Current Plan</p>
          <div className="flex items-center gap-3">
            <p className="text-3xl font-bold text-white">{formatPlanName(ownerPlan)}</p>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              ownerPlan === 'partner_pro' || ownerPlan === 'partner-pro'
                ? 'bg-purple-500/20 text-purple-300'
                : ownerPlan === 'pro'
                ? 'bg-blue-500/20 text-blue-300'
                : 'bg-gray-500/20 text-gray-300'
            }`}>
              {formatPlanName(ownerPlan)}
            </span>
          </div>
        </div>

        {/* Plan Limits (Inherited) */}
        <div className="space-y-3">
          <p className="text-gray-400 text-sm font-medium mb-3">Inherited Limits</p>
          
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-400">Max Agents</span>
            <span className="text-white font-medium">{limits.agents}</span>
          </div>
          
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-400">Voice Agents</span>
            <span className="text-white font-medium">{limits.voice}</span>
          </div>
          
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-400">Multi-Tenant</span>
            <span className={`font-medium ${limits.multiTenant ? 'text-green-400' : 'text-gray-500'}`}>
              {limits.multiTenant ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-white/5 rounded-lg p-4">
        <p className="text-gray-400 text-sm">
          <strong className="text-white">Need to upgrade?</strong> Plans are managed at the user level. 
          Click &quot;View Owner Profile&quot; above to change {ownerData?.name || 'the owner'}&apos;s plan and update this organization&apos;s capabilities.
        </p>
      </div>
    </div>
  );
};

// Billing Tab Component
const BillingTab = ({ orgId }) => (
  <div className="space-y-4">
    <div className="text-center py-8">
      <CurrencyDollar size={48} className="text-gray-500 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-white mb-2">Billing History</h3>
      <p className="text-gray-400">Stripe integration coming soon</p>
    </div>
  </div>
);

// UsersThree Tab Component - Shows actual organization members
const UsersTab = ({ orgId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrgUsers();
  }, [orgId]);

  const fetchOrgUsers = async () => {
    try {
      setLoading(true);
      // Fetch users that are members of this org
      const data = await adminApiGet(`/api/admin/organizations/${orgId}/members`);
      setUsers(data.members || []);
    } catch (error) {
      console.error('Error fetching org users:', error);
      // Fallback: no members endpoint yet, show empty state
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8">
        <UsersThree size={48} className="text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">No UsersThree Found</h3>
        <p className="text-gray-400">This organization has no active members</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {users.map((member) => (
        <div key={member.user_id || member.id} className="bg-white/5 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                <span className="text-white font-medium">
                  {member.name?.charAt(0) || member.email?.charAt(0) || 'U'}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">{member.name || 'Unknown'}</p>
                <p className="text-gray-400 text-sm">{member.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-2 py-1 text-xs rounded-full ${
                member.role === 'owner' || member.role === 'admin'
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                  : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
              }`}>
                {member.role || 'member'}
              </span>
              <button
                onClick={() => navigate(`/admin/users/${member.user_id || member.id}`)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="View user details"
              >
                <Eye size={16} className="text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Agents Tab Component - Simplified view without long descriptions
const AgentsTab = ({ org }) => (
  <div className="space-y-4">
    {org?.agents && org.agents.length > 0 ? (
      org.agents.map((agent) => (
        <div key={agent.agent_id} className="bg-white/5 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Robot size={20} className="text-[#6366F1]" />
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-white font-medium">{agent.name || 'Unnamed Agent'}</h4>
                  {agent.voice_enabled && (
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full border border-purple-500/30">
                      Voice
                    </span>
                  )}
                  <span className={`px-2 py-0.5 text-xs rounded-full border ${
                    agent.status === 'active' 
                      ? 'bg-green-500/20 text-green-300 border-green-500/30'
                      : 'bg-gray-500/20 text-gray-300 border-gray-500/30'
                  }`}>
                    {agent.status || 'draft'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  ID: {agent.agent_id?.substring(0, 8)}... • Created: {agent.created_at ? new Date(agent.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
            
            <button
              onClick={() => window.open(`/partner-pro/ai-workforce/agents/${agent.agent_id}/edit`, '_blank')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="View agent details"
            >
              <ArrowSquareOut size={16} className="text-gray-400" />
            </button>
          </div>
        </div>
      ))
    ) : (
      <div className="text-center py-8">
        <Robot size={48} className="text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">No Agents</h3>
        <p className="text-gray-400">This organization has not created any AI agents yet</p>
      </div>
    )}
  </div>
);

// Usage Tab Component
const UsageTab = ({ orgId, stats }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 gap-6">
      <div className="bg-white/5 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
          <Lightning size={16} />
          LLM Usage (30d)
        </h3>
        <p className="text-3xl font-bold text-white">{stats ? (stats.llmTokens30d / 1000).toFixed(1) + 'K' : '0'}</p>
        <p className="text-xs text-gray-400 mt-1">Tokens consumed</p>
      </div>

      <div className="bg-white/5 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
          <Microphone size={16} />
          Voice Usage (30d)
        </h3>
        <p className="text-3xl font-bold text-white">{stats?.voiceMinutes30d || 0}</p>
        <p className="text-xs text-gray-400 mt-1">Minutes used</p>
      </div>
    </div>
  </div>
);

// GearSix Tab Component
const SettingsTab = ({ org, onUpdate }) => (
  <div className="space-y-6">
    <div className="bg-white/5 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-white mb-4">Danger Zone</h3>
      <div className="space-y-3">
        <button className="w-full px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors text-left">
          Reset API Keys
        </button>
        <button className="w-full px-4 py-2 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-left">
          Delete Organization
        </button>
      </div>
    </div>
  </div>
);

export default OrganizationDetail;
